import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TournamentsService } from '@app/services/tournaments/tournaments.service';
import { Dictionary } from '@app/_models/shared.model';
import * as moment from 'moment';
import Utilities from '@app/_helpers/utilities';
import { MatSnackBar, MatDialog } from '@angular/material';
import { Location } from '@angular/common';
import { AddSponsorComponent } from '@app/components/shared/add-sponsor/add-sponsor.component';
import { Sponsor } from '@app/_models/giveaway';
import { FieldCapacity } from '@app/_models/facilities';
import { Router } from '@angular/router';

@Component({
  selector: 'create-tournament',
  templateUrl: './create-tournament.component.html',
  styleUrls: ['./create-tournament.component.scss'],
})
export class CreateTournamentComponent implements OnInit {
  constructor(
    private _builder: FormBuilder,
    private _tournamentService: TournamentsService,
    private _snackBar: MatSnackBar,
    private _location: Location,
    private _dialog: MatDialog,
    private _router: Router
  ) { }
  sponsors: Sponsor[] = [];
  fieldsCapacities: FieldCapacity[] = [];
  validFormats = ['H:mm', 'HH:mm', 'HH', 'H'];
  days: Dictionary[] = [
    {
      name: 'Lunes',
      value: 1,
    },
    {
      name: 'Martes',
      value: 2,
    },
    {
      name: 'Miercoles',
      value: 3,
    },
    {
      name: 'Jueves',
      value: 4,
    },
    {
      name: 'Viernes',
      value: 5,
    },
    {
      name: 'Sabado',
      value: 6,
    },
    {
      name: 'Domingo',
      value: 7,
    },
  ];

  newTournamentForm = this._builder.group({
    name: ['', [Validators.required]],
    description: ['', [Validators.required]],
    teamQuantity: [
      '',
      [Validators.required, Validators.max(20), Validators.min(4)],
    ],
    teamSize: [
      '',
      [Validators.required, Validators.max(11), Validators.min(5)],
    ],
    image: ['', [Validators.required]],
    startDate: ['', [Validators.required]],
    days: ['', Validators.required],
    entryCost: [0, [Validators.required]],
    showEntryCost: [false],
    state: [false],
    matchTimeFrom: ['', [Validators.required]],
    matchTimeTo: [
      {
        value: '',
        disabled: true,
      },
      [Validators.required],
    ],
  });
  imgString: string;

  ngOnInit(): void {
    this._tournamentService.getFieldCapacities().subscribe(capacities => {
      this.fieldsCapacities = capacities;
    });
  }

  checkTimePerDay() {
    const teamSize: FieldCapacity = this.newTournamentForm.get('teamSize')
      .value;
    const teamQuantity = this.newTournamentForm.get('teamQuantity').value;
    const matchTimeFrom = this.newTournamentForm.get('matchTimeFrom').value;
    if (teamSize && teamQuantity && matchTimeFrom) {
      // Formatear la hora del input
      this.newTournamentForm.patchValue({
        matchTimeFrom: moment(
          this.newTournamentForm.get('matchTimeFrom').value,
          this.validFormats
        ).format('HH:mm'),
      });
      let fieldsCount: number = 0;
      this._tournamentService
        .getFieldsByCapacity(teamSize.id)
        .subscribe(fields => {
          if (fields.length) {
            fieldsCount = fields.length;
            const totalHours = Math.ceil(teamQuantity / 2 / fieldsCount);
            this.newTournamentForm.patchValue({
              matchTimeTo: moment(matchTimeFrom, this.validFormats)
                .add(totalHours, 'hours')
                .format('HH:mm'),
            });
          } else {
            this._snackBar.open(
              'No existen canchas para esta cantidad de jugadores',
              '',
              {
                duration: 5000,
              }
            );
          }
        });
    }
  }

  uploadNewTournamentImg(): void {
    document.getElementById('tournamentFileInput').click();
  }

  async handleNewImgUpload(file: File): Promise<string> {
    const imgString = await Utilities.toBase64(file);
    this.newTournamentForm.patchValue({
      image: file,
    });
    this.imgString = imgString;
    return '';
  }

  constructTournament(): FormData {
    const form = this.newTournamentForm;
    const tournament = new FormData();
    tournament.append('name', form.get('name').value);
    tournament.append('teams_quantity', form.get('teamQuantity').value);
    tournament.append(
      'team_player_quantity',
      (form.get('teamSize').value as FieldCapacity).id.toString()
    );
    tournament.append(
      'start_date',
      moment(form.get('startDate').value)
        .toISOString()
        .split('T')[0]
    );
    tournament.append('start_time', form.get('matchTimeFrom').value);
    tournament.append('end_time', form.get('matchTimeTo').value);
    tournament.append(
      'public_registration_fee',
      form.get('showEntryCost').value
    );
    tournament.append('registration_fee', form.get('entryCost').value);
    tournament.append('description', form.get('description').value);
    tournament.append('state', (form.get('state').value ? 1 : 0).toString());
    tournament.append('image', form.get('image').value);
    form.get('days').value.forEach(day => {
      tournament.append('days', day.value.toString());
    });
    this.sponsors.forEach(sponsor => {
      tournament.append('sponsors', sponsor.id.toString());
    });
    return tournament;
  }

  removeSponsor(idx: number) {
    this.sponsors.splice(idx, 1);
  }

  openSponsorDialog() {
    const sponsorDialog = this._dialog.open(AddSponsorComponent, {
      width: '800px',
    });

    const addedSponsorEvent = sponsorDialog.componentInstance.onAddedSponsors.subscribe(
      (sponsors: Sponsor[]) => {
        this.sponsors = this.mergeWithoutDuplicated(this.sponsors, sponsors);
      }
    );

    sponsorDialog.afterClosed().subscribe(() => {
      addedSponsorEvent.unsubscribe();
    });
  }

  saveTournament(): void {
    try {
      const isFromTimeValid = moment(
        this.newTournamentForm.get('matchTimeFrom').value,
        this.validFormats,
        true
      ).isValid();
      const isToTimeValid = moment(
        this.newTournamentForm.get('matchTimeTo').value,
        this.validFormats,
        true
      ).isValid();
      if (this.newTournamentForm.valid && isFromTimeValid && isToTimeValid) {
        const tournament = this.constructTournament();
        this._tournamentService
          .createTournament(tournament)
          .subscribe(newTournament => {
            this._snackBar.open('Torneo creado correctamente', '', {
              duration: 3000,
            });
            this._router.navigate(['/tournaments/']);
          });
      }
    } catch (err) {
      console.log(err);
    }
  }

  returnToList() {
    this._location.back();
  }

  // Utiltities
  mergeWithoutDuplicated(...arrays): Sponsor[] {
    let jointArray = [];
    arrays.forEach((entireArray: Array<Sponsor>) => {
      jointArray = [...jointArray, ...entireArray];
    });
    const mergedArray = jointArray.filter((sponsor: Sponsor, idx) => {
      const foundIdx = jointArray.findIndex((e: Sponsor) => {
        return e.id === sponsor.id;
      });
      return idx === foundIdx;
    });
    return mergedArray;
  }
}
